import React, { useState, useEffect, useCallback } from "react";
import { Drawer, Flex, Typography, Tag, Button, Tabs, Card, Row, Col, Statistic, Table, Space, Avatar, Divider, Progress, Modal, Input, Select, Switch, message, Spin, Tooltip } from "antd";
import { BankOutlined, UserOutlined, MailOutlined, PhoneOutlined, CalendarOutlined, TeamOutlined, CheckCircleOutlined, FileTextOutlined, EditOutlined, KeyOutlined, PlusOutlined, DownloadOutlined, CopyOutlined, SyncOutlined, FolderOutlined } from "@ant-design/icons";
import InrOutlined from "../../general/components/common/InrOutlined";
import superAdminService from "../services/superAdminService";

const { Text } = Typography;

export function getStatusTagColor(statusName, company) {
  const s = String(statusName || "")
    .toLowerCase()
    .trim();
  const convertedSet = new Set((company?.convertedPageStatuses || []).map((x) => String(x).toLowerCase().trim()));
  const interestedSet = new Set((company?.interestedPageStatuses || []).map((x) => String(x).toLowerCase().trim()));
  const dnpSet = new Set((company?.dnpPageStatuses || []).map((x) => String(x).toLowerCase().trim()));

  if (convertedSet.has(s) || s === "converted") return "green";
  if (interestedSet.has(s) || s === "follow up" || s === "interested" || s === "connected" || s === "contacted" || s === "pipeline" || s === "details shared") {
    return "blue";
  }
  if (dnpSet.has(s) || s.includes("not") || s.includes("invalid") || s.includes("lost") || s === "dnp") return "default";
  if (s === "new" || s === "fresh" || s === "pending") return "orange";
  return "purple";
}

export function calculateDaysRemaining(trialEndDate) {
  if (!trialEndDate) return null;
  const end = new Date(trialEndDate);
  if (Number.isNaN(end.getTime())) return null;
  const now = new Date();
  const diffTime = end - now;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

export function exportCompanyDossierJson(company, metrics, payments, totalPaidRevenue) {
  const exportData = {
    exportedAt: new Date().toISOString(),
    companyProfile: company,
    liveTenantMetrics: metrics,
    billingHistory: payments,
    totalPaidRevenue,
  };
  const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(exportData, null, 2));
  const downloadAnchor = document.createElement("a");
  downloadAnchor.setAttribute("href", dataStr);
  downloadAnchor.setAttribute("download", `Dossier_${company.companyCode || "company"}_${Date.now()}.json`);
  document.body.appendChild(downloadAnchor);
  downloadAnchor.click();
  downloadAnchor.remove();
}

const EMPLOYEE_TABLE_COLUMNS = [
  {
    title: "Name & Email",
    key: "name",
    render: (_, emp) => (
      <Flex vertical gap={2}>
        <Text strong style={{ fontSize: "0.85rem", color: "#1e293b" }}>
          {emp.name || "—"}
        </Text>
        <Text type="secondary" style={{ fontSize: "0.78rem" }}>
          {emp.email || "—"}
        </Text>
      </Flex>
    ),
  },
  {
    title: "Phone",
    dataIndex: "phone",
    key: "phone",
    render: (ph) => <Text style={{ fontSize: "0.82rem" }}>{ph || "—"}</Text>,
  },
  {
    title: "Role",
    dataIndex: "role",
    key: "role",
    render: (r) => (
      <Tag color="blue" style={{ borderRadius: 6, textTransform: "capitalize" }}>
        {r || "Sales Rep"}
      </Tag>
    ),
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (st) => (
      <Tag color={st === "inactive" ? "error" : "success"} style={{ borderRadius: 6 }}>
        {st || "Active"}
      </Tag>
    ),
  },
];

const PAYMENT_TABLE_COLUMNS = [
  {
    title: "Date",
    dataIndex: "createdAt",
    key: "createdAt",
    width: 110,
    render: (dt) => <Text style={{ fontSize: "0.82rem" }}>{dt ? new Date(dt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</Text>,
  },
  {
    title: "Amount",
    dataIndex: "amount",
    key: "amount",
    width: 100,
    render: (amt) => (
      <Text strong style={{ color: "#059669", fontSize: "0.9rem" }}>
        ₹{Number(amt || 0).toLocaleString("en-IN")}
      </Text>
    ),
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: 90,
    render: (st) => (
      <Tag color={st === "paid" ? "success" : "warning"} style={{ borderRadius: 6, fontWeight: 600, textTransform: "uppercase" }}>
        {st}
      </Tag>
    ),
  },
  {
    title: "Payment ID",
    dataIndex: "razorpayPaymentId",
    key: "razorpayPaymentId",
    render: (id) => (
      <Text code style={{ color: "#7c3aed", fontSize: "0.78rem" }}>
        {id || "—"}
      </Text>
    ),
  },
  {
    title: "Method",
    dataIndex: "paymentMethod",
    key: "paymentMethod",
    render: (m) => <Tag style={{ textTransform: "uppercase", fontSize: "0.72rem" }}>{m || "N/A"}</Tag>,
  },
];

function DossierDrawerHeader({ company, loading, onCopyCode, onExport, onRefresh }) {
  const isPaidOrActive = company.status === "Active" || company.status === "paid";
  return (
    <Flex justify="space-between" align="center" style={{ width: "100%", paddingRight: 8 }}>
      <Flex align="center" gap={12}>
        <Avatar size={40} icon={<BankOutlined />} style={{ backgroundColor: "#f5f3ff", color: "#7c3aed" }} />
        <Flex vertical>
          <Flex align="center" gap={8}>
            <Text strong style={{ fontSize: "1.1rem", color: "#0f172a" }}>
              {company.companyName || "Company Details"}
            </Text>
            <Tag color={isPaidOrActive ? "success" : "purple"} style={{ borderRadius: 6, fontWeight: 600 }}>
              {company.status || "Free-Trial"}
            </Tag>
          </Flex>
          <Flex align="center" gap={6}>
            <Text code style={{ fontSize: "0.8rem", color: "#64748b" }}>
              {company.companyCode}
            </Text>
            <Tooltip title="Copy Company Code">
              <CopyOutlined onClick={onCopyCode} style={{ cursor: "pointer", color: "#94a3b8", fontSize: "0.8rem" }} />
            </Tooltip>
          </Flex>
        </Flex>
      </Flex>

      <Flex align="center" gap={8}>
        <Button size="small" icon={<DownloadOutlined />} onClick={onExport}>
          Export JSON
        </Button>
        <Button size="small" icon={<SyncOutlined spin={loading} />} onClick={onRefresh}>
          Refresh
        </Button>
      </Flex>
    </Flex>
  );
}

function DossierKpiStrip({ totalLeads, convRate, totalEmployees, totalPaidRevenue }) {
  return (
    <Row gutter={[12, 12]}>
      <Col span={6}>
        <Card bordered={false} styles={{ body: { padding: "12px 14px" } }} style={{ borderRadius: 10, background: "#ffffff" }}>
          <Statistic
            title={
              <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                Total Leads
              </Text>
            }
            value={totalLeads}
            valueStyle={{ fontSize: "1.25rem", fontWeight: 700, color: "#2563eb" }}
            prefix={<FileTextOutlined style={{ fontSize: "1rem", marginRight: 4 }} />}
          />
        </Card>
      </Col>

      <Col span={6}>
        <Card bordered={false} styles={{ body: { padding: "12px 14px" } }} style={{ borderRadius: 10, background: "#ffffff" }}>
          <Statistic
            title={
              <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                Conversion Rate
              </Text>
            }
            value={`${convRate}%`}
            valueStyle={{ fontSize: "1.25rem", fontWeight: 700, color: "#10b981" }}
            prefix={<CheckCircleOutlined style={{ fontSize: "1rem", marginRight: 4 }} />}
          />
        </Card>
      </Col>

      <Col span={6}>
        <Card bordered={false} styles={{ body: { padding: "12px 14px" } }} style={{ borderRadius: 10, background: "#ffffff" }}>
          <Statistic
            title={
              <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                Sales Team
              </Text>
            }
            value={totalEmployees}
            valueStyle={{ fontSize: "1.25rem", fontWeight: 700, color: "#7c3aed" }}
            prefix={<TeamOutlined style={{ fontSize: "1rem", marginRight: 4 }} />}
          />
        </Card>
      </Col>

      <Col span={6}>
        <Card bordered={false} styles={{ body: { padding: "12px 14px" } }} style={{ borderRadius: 10, background: "#ffffff" }}>
          <Statistic
            title={
              <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                Lifetime Value
              </Text>
            }
            value={`₹${totalPaidRevenue.toLocaleString("en-IN")}`}
            valueStyle={{ fontSize: "1.25rem", fontWeight: 700, color: "#059669" }}
          />
        </Card>
      </Col>
    </Row>
  );
}

function DossierActionBar({ onExtendTrial, onChangeStatus, onResetPassword, onAssignRmClick, company }) {
  return (
    <Card bordered={false} styles={{ body: { padding: "12px 16px" } }} style={{ borderRadius: 10, background: "#ffffff" }}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
        <Flex align="center" gap={8} wrap="wrap">
          <Text strong style={{ fontSize: "0.82rem", color: "#334155" }}>
            Superadmin Actions:
          </Text>
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={onExtendTrial} style={{ borderRadius: 6, backgroundColor: "#7c3aed" }}>
            Extend Trial
          </Button>
          <Button size="small" icon={<EditOutlined />} onClick={onChangeStatus} style={{ borderRadius: 6 }}>
            Change Status
          </Button>
          <Button size="small" icon={<KeyOutlined />} onClick={onResetPassword} style={{ borderRadius: 6 }}>
            Reset Admin Password
          </Button>
        </Flex>

        {onAssignRmClick && (
          <Button size="small" icon={<UserOutlined />} onClick={() => onAssignRmClick(company)} style={{ borderRadius: 6, borderColor: "#7c3aed", color: "#7c3aed" }}>
            {company.relationshipManager?.name ? "Reassign RM" : "Assign RM"}
          </Button>
        )}
      </Flex>
    </Card>
  );
}

function DossierIdentityGrid({ company }) {
  return (
    <Row gutter={[16, 16]}>
      <Col span={12}>
        <Flex vertical gap={4}>
          <Text type="secondary" style={{ fontSize: "0.78rem" }}>
            Registered Administrator
          </Text>
          <Text strong style={{ fontSize: "0.95rem", color: "#0f172a" }}>
            {company.name || "—"}
          </Text>
          <Flex align="center" gap={6} style={{ color: "#475569", fontSize: "0.82rem" }}>
            <MailOutlined style={{ color: "#94a3b8" }} />
            <span>{company.email || "—"}</span>
          </Flex>
          <Flex align="center" gap={6} style={{ color: "#475569", fontSize: "0.82rem" }}>
            <PhoneOutlined style={{ color: "#94a3b8" }} />
            <span>{company.mobile || "—"}</span>
          </Flex>
        </Flex>
      </Col>

      <Col span={12}>
        <Flex vertical gap={4}>
          <Text type="secondary" style={{ fontSize: "0.78rem" }}>
            Tenant Configuration
          </Text>
          <Flex align="center" justify="space-between">
            <Text style={{ fontSize: "0.85rem", color: "#64748b" }}>Industry:</Text>
            <Text strong style={{ fontSize: "0.85rem" }}>
              {company.industry || "General / Other"}
            </Text>
          </Flex>
          <Flex align="center" justify="space-between">
            <Text style={{ fontSize: "0.85rem", color: "#64748b" }}>Team Tier:</Text>
            <Tag color="blue">{company.teamSize || "1-10 Employees"}</Tag>
          </Flex>
          <Flex align="center" justify="space-between">
            <Text style={{ fontSize: "0.85rem", color: "#64748b" }}>Registered Date:</Text>
            <Text style={{ fontSize: "0.82rem" }}>{company.createdAt ? new Date(company.createdAt).toLocaleDateString() : "—"}</Text>
          </Flex>
        </Flex>
      </Col>
    </Row>
  );
}

function DossierCrmAccessCard({ company, togglingCrm, onToggleCrm }) {
  const isCrmActive = company.isCrmEnabled !== false;
  return (
    <Card size="small" style={{ borderRadius: 8, background: "#ffffff", border: "1px solid #e2e8f0" }}>
      <Flex align="center" justify="space-between" gap={16}>
        <Flex vertical gap={2}>
          <Text strong style={{ fontSize: "0.85rem", color: "#0f172a" }}>
            CRM Suite Access
          </Text>
          <Text type="secondary" style={{ fontSize: "0.75rem", color: "#64748b" }}>
            Clients, SLAs, NDAs, AMCs, Payments, Tickets, & Projects modules
          </Text>
        </Flex>

        <Switch checked={isCrmActive} loading={togglingCrm} onChange={onToggleCrm} />
      </Flex>
    </Card>
  );
}

function DossierSubscriptionCard({ company, daysRemaining }) {
  const isDaysRemainingValid = daysRemaining !== null && daysRemaining > 0;
  return (
    <Flex vertical gap={8}>
      <Text strong style={{ fontSize: "0.9rem", color: "#0f172a" }}>
        Subscription & Trial Lifecycle
      </Text>
      <Row gutter={[12, 12]}>
        <Col span={12}>
          <Card size="small" style={{ borderRadius: 8, background: "#f8fafc" }}>
            <Flex justify="space-between" align="center">
              <Flex vertical gap={2}>
                <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                  Trial Start
                </Text>
                <Text strong style={{ fontSize: "0.85rem" }}>
                  {company.trialStartDate ? new Date(company.trialStartDate).toLocaleDateString() : "Not Activated"}
                </Text>
              </Flex>
              <CalendarOutlined style={{ color: "#94a3b8", fontSize: "1.2rem" }} />
            </Flex>
          </Card>
        </Col>

        <Col span={12}>
          <Card size="small" style={{ borderRadius: 8, background: "#f8fafc" }}>
            <Flex justify="space-between" align="center">
              <Flex vertical gap={2}>
                <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                  Trial Expiration
                </Text>
                <Text strong style={{ fontSize: "0.85rem" }}>
                  {company.trialEndDate ? new Date(company.trialEndDate).toLocaleDateString() : "—"}
                </Text>
              </Flex>
              <Tag color={isDaysRemainingValid ? "success" : "error"} style={{ borderRadius: 6, fontWeight: 600 }}>
                {(() => {
                  if (daysRemaining === null) return "Active";
                  return daysRemaining > 0 ? `${daysRemaining} Days Left` : "Expired";
                })()}
              </Tag>
            </Flex>
          </Card>
        </Col>
      </Row>
    </Flex>
  );
}

function DossierLeadFunnelCard({ metrics, convRate, company }) {
  const hasCustomBreakdown = Array.isArray(metrics.statusBreakdown) && metrics.statusBreakdown.length > 0;
  return (
    <Flex vertical gap={8}>
      <Flex justify="space-between" align="center">
        <Text strong style={{ fontSize: "0.9rem", color: "#0f172a" }}>
          Live Pipeline & Lead Breakdown (Tenant Database)
        </Text>
        <Text type="secondary" style={{ fontSize: "0.75rem" }}>
          Conversion: {convRate}%
        </Text>
      </Flex>

      <Progress percent={convRate} status="active" strokeColor={{ "0%": "#10b981", "100%": "#059669" }} style={{ marginBottom: 4 }} />

      <Flex gap={8} wrap="wrap">
        {hasCustomBreakdown ? (
          metrics.statusBreakdown.map((item) => (
            <Tag key={item.status} color={getStatusTagColor(item.status, company)} style={{ borderRadius: 6, padding: "2px 8px" }}>
              {item.status}: <b>{(item.count || 0).toLocaleString("en-IN")}</b>
            </Tag>
          ))
        ) : (
          <>
            <Tag color="green" style={{ borderRadius: 6 }}>
              Converted: <b>{metrics.convertedLeads || 0}</b>
            </Tag>
            <Tag color="blue" style={{ borderRadius: 6 }}>
              Interested: <b>{metrics.interestedLeads || 0}</b>
            </Tag>
            <Tag color="orange" style={{ borderRadius: 6 }}>
              Fresh/New: <b>{metrics.freshLeads || 0}</b>
            </Tag>
            <Tag color="default" style={{ borderRadius: 6 }}>
              DNP: <b>{metrics.dnpLeads || 0}</b>
            </Tag>
          </>
        )}

        <Tag color="purple" style={{ borderRadius: 6, borderStyle: "dashed" }}>
          Quotations: <b>{metrics.totalQuotations || 0}</b>
        </Tag>
        <Tag color="cyan" style={{ borderRadius: 6, borderStyle: "dashed" }}>
          Invoices: <b>{metrics.totalInvoices || 0}</b>
        </Tag>
        <Tag color="geekblue" style={{ borderRadius: 6, borderStyle: "dashed" }}>
          Proposals: <b>{metrics.totalProposals || 0}</b>
        </Tag>
      </Flex>
    </Flex>
  );
}

function DossierRmCard({ company, onAssignRmClick }) {
  const rm = company.relationshipManager;
  return (
    <Flex vertical gap={6}>
      <Text strong style={{ fontSize: "0.9rem", color: "#0f172a" }}>
        Relationship Manager Assignment
      </Text>
      {rm?.name ? (
        <Card size="small" style={{ borderRadius: 8, background: "#faf5ff", borderColor: "#e9d5ff" }}>
          <Flex justify="space-between" align="center">
            <Flex align="center" gap={12}>
              <Avatar size={36} icon={<UserOutlined />} style={{ backgroundColor: "#7c3aed" }} />
              <Flex vertical>
                <Text strong style={{ color: "#581c87" }}>
                  {rm.name}
                </Text>
                <Text type="secondary" style={{ fontSize: "0.78rem" }}>
                  {rm.email} • {rm.phone}
                </Text>
              </Flex>
            </Flex>
            <Flex vertical align="flex-end">
              <Text style={{ fontSize: "0.75rem", color: "#6b21a8" }}>{rm.workingDays || "Mon - Sat"}</Text>
              <Text type="secondary" style={{ fontSize: "0.72rem" }}>
                {rm.workingHours || "9:30 AM - 6:30 PM"}
              </Text>
            </Flex>
          </Flex>
        </Card>
      ) : (
        <Flex align="center" justify="space-between" style={{ padding: "10px 14px", background: "#f1f5f9", borderRadius: 8 }}>
          <Text type="secondary" style={{ fontStyle: "italic", fontSize: "0.82rem" }}>
            No Relationship Manager assigned to this tenant yet.
          </Text>
          {onAssignRmClick && (
            <Button size="small" type="primary" onClick={() => onAssignRmClick(company)}>
              Assign Now
            </Button>
          )}
        </Flex>
      )}
    </Flex>
  );
}

function DossierProfileTab({ company, togglingCrm, onToggleCrm, daysRemaining, metrics, convRate, onAssignRmClick }) {
  return (
    <Flex vertical gap={16} style={{ paddingTop: 8 }}>
      <DossierIdentityGrid company={company} />
      <DossierCrmAccessCard company={company} togglingCrm={togglingCrm} onToggleCrm={onToggleCrm} />
      <Divider style={{ margin: "8px 0" }} />
      <DossierSubscriptionCard company={company} daysRemaining={daysRemaining} />
      <Divider style={{ margin: "8px 0" }} />
      <DossierLeadFunnelCard metrics={metrics} convRate={convRate} company={company} />
      <Divider style={{ margin: "8px 0" }} />
      <DossierRmCard company={company} onAssignRmClick={onAssignRmClick} />
    </Flex>
  );
}

function ExtendTrialModal({ visible, onCancel, onOk, confirmLoading, companyName, extendDays, setExtendDays }) {
  return (
    <Modal title="Extend Free Trial" open={visible} onCancel={onCancel} onOk={onOk} confirmLoading={confirmLoading} okText="Extend Trial" destroyOnClose>
      <Flex vertical gap={12} style={{ marginTop: 16 }}>
        <Text>
          Select how many days to add to <b>{companyName}</b>'s trial period:
        </Text>
        <Select
          value={extendDays}
          onChange={setExtendDays}
          style={{ width: "100%" }}
          options={[
            { value: 7, label: "+ 7 Days (1 Week)" },
            { value: 14, label: "+ 14 Days (2 Weeks)" },
            { value: 30, label: "+ 30 Days (1 Month)" },
            { value: 60, label: "+ 60 Days (2 Months)" },
          ]}
        />
      </Flex>
    </Modal>
  );
}

function StatusChangeModal({ visible, onCancel, onOk, confirmLoading, companyName, selectedStatus, setSelectedStatus }) {
  return (
    <Modal title="Change Account Status" open={visible} onCancel={onCancel} onOk={onOk} confirmLoading={confirmLoading} okText="Update Status" destroyOnClose>
      <Flex vertical gap={12} style={{ marginTop: 16 }}>
        <Text>
          Select account lifecycle status for <b>{companyName}</b>:
        </Text>
        <Select
          value={selectedStatus}
          onChange={setSelectedStatus}
          style={{ width: "100%" }}
          options={[
            { value: "Free-Trial", label: "Free-Trial (Active 7-day trial)" },
            { value: "Active", label: "Active (Paid Subscription)" },
            { value: "Suspended", label: "Suspended (Blocked Access)" },
            { value: "Free-Trial-Request", label: "Free-Trial-Request (Pending Approval)" },
          ]}
        />
      </Flex>
    </Modal>
  );
}

function ResetPasswordModal({ visible, onCancel, onOk, confirmLoading, companyName, companyEmail, newPassword, setNewPassword }) {
  return (
    <Modal title="Reset Company Administrator Password" open={visible} onCancel={onCancel} onOk={onOk} confirmLoading={confirmLoading} okText="Set New Password" destroyOnClose>
      <Flex vertical gap={12} style={{ marginTop: 16 }}>
        <Text>
          Enter a new password for admin <b>{companyName}</b> ({companyEmail}):
        </Text>
        <Input.Password placeholder="Enter new password (min 4 characters)" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
      </Flex>
    </Modal>
  );
}

function CompanyDossierDrawer({ visible, companyCode, onClose, onCompanyUpdated, onAssignRmClick }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState("overview");

  // CRM Suite Access State
  const [togglingCrm, setTogglingCrm] = useState(false);

  // Extend Trial Modal State
  const [extendModalVisible, setExtendModalVisible] = useState(false);
  const [extendDays, setExtendDays] = useState(7);
  const [extending, setExtending] = useState(false);

  // Status Change Modal State
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("Active");
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Password Reset Modal State
  const [pwdModalVisible, setPwdModalVisible] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [resettingPwd, setResettingPwd] = useState(false);

  // Fetch Full Company Details & Live Tenant DB Metrics
  const fetchDetails = useCallback(async () => {
    if (!companyCode) return;
    setLoading(true);
    try {
      const res = await superAdminService.getCompanyDetails(companyCode);
      if (res?.success) {
        setData(res);
      }
    } catch (err) {
      console.error("Failed to load company dossier:", err);
      message.error(err.message || "Failed to load company dossier.");
    } finally {
      setLoading(false);
    }
  }, [companyCode]);

  useEffect(() => {
    if (visible && companyCode) {
      fetchDetails();
    } else {
      setData(null);
    }
  }, [visible, companyCode, fetchDetails]);

  const company = data?.company || {};
  const metrics = data?.metrics || {};
  const payments = data?.payments || [];
  const totalPaidRevenue = data?.totalPaidRevenue || 0;

  const totalLeads = metrics.totalLeads || 0;
  const convertedLeads = metrics.convertedLeads || 0;
  const convRate = totalLeads > 0 ? Math.round((convertedLeads / totalLeads) * 100) : 0;
  const daysRemaining = calculateDaysRemaining(company.trialEndDate);

  const handleCopyCode = () => {
    if (company.companyCode) {
      navigator.clipboard.writeText(company.companyCode);
      message.success(`Copied "${company.companyCode}" to clipboard!`);
    }
  };

  const handleExtendTrial = async () => {
    setExtending(true);
    try {
      const res = await superAdminService.extendTrial(company.companyCode, extendDays);
      if (res?.success) {
        message.success(res.message || `Extended trial by ${extendDays} days!`);
        setExtendModalVisible(false);
        fetchDetails();
        if (onCompanyUpdated) onCompanyUpdated();
      }
    } catch (err) {
      message.error(err.message || "Failed to extend trial.");
    } finally {
      setExtending(false);
    }
  };

  const handleUpdateStatus = async () => {
    setUpdatingStatus(true);
    try {
      const res = await superAdminService.updateStatus(company.companyCode, selectedStatus);
      if (res?.success) {
        message.success(`Company status updated to ${selectedStatus}!`);
        setStatusModalVisible(false);
        fetchDetails();
        if (onCompanyUpdated) onCompanyUpdated();
      }
    } catch (err) {
      message.error(err.message || "Failed to update status.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 4) {
      message.warning("Password must be at least 4 characters.");
      return;
    }
    setResettingPwd(true);
    try {
      const res = await superAdminService.resetPassword(company.companyCode, newPassword);
      if (res?.success) {
        message.success(res.message || "Password updated successfully!");
        setPwdModalVisible(false);
        setNewPassword("");
      }
    } catch (err) {
      message.error(err.message || "Failed to reset password.");
    } finally {
      setResettingPwd(false);
    }
  };

  const handleToggleCrm = async (checked) => {
    setTogglingCrm(true);
    try {
      const res = await superAdminService.toggleCompanyCrm(company.companyCode, checked);
      if (res?.success) {
        message.success(res.message || `CRM Suite ${checked ? "enabled" : "disabled"}!`);
        setData((prev) => ({
          ...prev,
          company: {
            ...prev?.company,
            isCrmEnabled: checked,
          },
        }));
        if (onCompanyUpdated) onCompanyUpdated();
      }
    } catch (err) {
      message.error(err.message || "Failed to update CRM access.");
    } finally {
      setTogglingCrm(false);
    }
  };

  const handleExportDossier = () => {
    exportCompanyDossierJson(company, metrics, payments, totalPaidRevenue);
    message.success("Dossier report downloaded successfully!");
  };

  return (
    <Drawer title={<DossierDrawerHeader company={company} loading={loading} onCopyCode={handleCopyCode} onExport={handleExportDossier} onRefresh={fetchDetails} />} placement="right" width={780} onClose={onClose} open={visible} destroyOnClose styles={{ body: { padding: "16px 24px", background: "#f8fafc" } }}>
      {loading && !data ? (
        <Flex justify="center" align="center" style={{ height: 350 }}>
          <Spin size="large" />
        </Flex>
      ) : (
        <Flex vertical gap={16}>
          <DossierKpiStrip totalLeads={totalLeads} convRate={convRate} totalEmployees={metrics.totalEmployees || 0} totalPaidRevenue={totalPaidRevenue} />

          <DossierActionBar
            onExtendTrial={() => setExtendModalVisible(true)}
            onChangeStatus={() => {
              setSelectedStatus(company.status || "Active");
              setStatusModalVisible(true);
            }}
            onResetPassword={() => setPwdModalVisible(true)}
            onAssignRmClick={onAssignRmClick}
            company={company}
          />

          <Card bordered={false} styles={{ body: { padding: "12px 20px" } }} style={{ borderRadius: 12, background: "#ffffff" }}>
            <Tabs
              activeKey={activeSubTab}
              onChange={setActiveSubTab}
              items={[
                {
                  key: "overview",
                  label: (
                    <Space size={6}>
                      <BankOutlined />
                      <span>Company Profile & Health</span>
                    </Space>
                  ),
                  children: <DossierProfileTab company={company} togglingCrm={togglingCrm} onToggleCrm={handleToggleCrm} daysRemaining={daysRemaining} metrics={metrics} convRate={convRate} onAssignRmClick={onAssignRmClick} />,
                },
                {
                  key: "team",
                  label: (
                    <Space size={6}>
                      <TeamOutlined />
                      <span>Sales Team ({metrics.employeesList?.length || metrics.totalEmployees || 0})</span>
                    </Space>
                  ),
                  children: <Table columns={EMPLOYEE_TABLE_COLUMNS} dataSource={metrics.employeesList || []} rowKey={(e, idx) => e._id || e.email || idx} pagination={{ pageSize: 5 }} size="small" locale={{ emptyText: "No sales team employees registered under this tenant." }} style={{ marginTop: 12 }} />,
                },
                {
                  key: "payments",
                  label: (
                    <Space size={6}>
                      <InrOutlined />
                      <span>Payment History ({payments.length})</span>
                    </Space>
                  ),
                  children: <Table columns={PAYMENT_TABLE_COLUMNS} dataSource={payments} rowKey={(p, idx) => p._id || p.razorpayPaymentId || idx} pagination={{ pageSize: 5 }} size="small" locale={{ emptyText: "No payment history recorded for this company." }} style={{ marginTop: 12 }} />,
                },
              ]}
            />
          </Card>
        </Flex>
      )}

      <ExtendTrialModal visible={extendModalVisible} onCancel={() => setExtendModalVisible(false)} onOk={handleExtendTrial} confirmLoading={extending} companyName={company.companyName} extendDays={extendDays} setExtendDays={setExtendDays} />

      <StatusChangeModal visible={statusModalVisible} onCancel={() => setStatusModalVisible(false)} onOk={handleUpdateStatus} confirmLoading={updatingStatus} companyName={company.companyName} selectedStatus={selectedStatus} setSelectedStatus={setSelectedStatus} />

      <ResetPasswordModal visible={pwdModalVisible} onCancel={() => setPwdModalVisible(false)} onOk={handleResetPassword} confirmLoading={resettingPwd} companyName={company.name} companyEmail={company.email} newPassword={newPassword} setNewPassword={setNewPassword} />
    </Drawer>
  );
}

export default CompanyDossierDrawer;
